import type { FieldDto } from '@lib/dto/envelope.dto'
import { nextRequiredId, viewportSlot } from './required-nav'

function fieldNode(id: string): HTMLElement | null {
  return window.document.getElementById(`sign-field-${id}`)
}

/** The fields, in the order they appear on the page (top to bottom), that are on the page. */
function inPageOrder(fields: readonly FieldDto[]): { id: string; node: HTMLElement }[] {
  return fields
    .flatMap((field) => {
      const node = fieldNode(field.id)
      return node ? [{ id: field.id, node }] : []
    })
    .sort((a, b) => a.node.getBoundingClientRect().top - b.node.getBoundingClientRect().top)
}

/** Brings a field to the middle of the screen and focuses it, so typing or Enter acts on it. */
function goTo(node: HTMLElement): void {
  node.scrollIntoView({ behavior: 'smooth', block: 'center' })
  node.focus({ preventScroll: true })
}

/** Goes to the first remaining field on the page ("Show me" by Finish). */
export function goToFirstField(fields: readonly FieldDto[]): void {
  const [first] = inPageOrder(fields)
  if (first) goTo(first.node)
}

/** Goes to the next remaining field from where the signer is, wrapping back to the top. */
function goToNextField(fields: readonly FieldDto[]): void {
  const items = inPageOrder(fields)
  const viewportHeight = window.innerHeight
  const id = nextRequiredId(items.map((item) => ({ id: item.id, slot: viewportSlot(item.node.getBoundingClientRect(), viewportHeight) })))
  const target = items.find((item) => item.id === id)
  if (target) goTo(target.node)
}

/**
 * The bar along the bottom while required fields remain: how many are left,
 * and a way to the next one. It covers every required field, not just
 * signatures, and stays up until the last one is filled, so a field the
 * signer scrolled past is never lost.
 */
export function NextRequiredFooter({
  remaining,
  hidden,
}: {
  /** The required fields still to fill (`remainingRequired`). */
  remaining: readonly FieldDto[]
  hidden?: boolean
}) {
  if (hidden || remaining.length === 0) {
    return null
  }
  const count = remaining.length
  return (
    <>
      {/* Room to scroll the end of the page, and Finish on a phone, out from under the bar. */}
      <div aria-hidden className="h-16" />
      <div className="sign-next-bar fixed inset-x-0 bottom-0 z-40">
        <div className="px-4 py-3 text-center">
          <button
            type="button"
            className="text-sm font-medium underline underline-offset-4"
            onClick={() => goToNextField(remaining)}
          >
            {count === 1 ? 'Go to the last required field' : `Go to next required field (${String(count)} left)`}
          </button>
        </div>
      </div>
    </>
  )
}
